import type { ConstructionSiteDocument, GFADocument } from "@dune/types";
import { Button, Chip } from "@dune/ui";
import {
	constructionSiteDocumentTypeLabels,
	gfaDocumentTypeLabels,
} from "@dune/vars";
import type { ColumnDef } from "@tanstack/react-table";
import { format } from "date-fns";
import { Download, FileText, Loader2, Trash2 } from "lucide-react";
import React, { useCallback } from "react";
import { useDocumentUrl } from "../../../../hooks/useDocumentUrl";
import { useUnlinkFileFromConstructionSiteSubmission } from "../../../../api/mutations/constructionSiteSubmission.mutations";
import { LinkBroken02 } from "@untitled-ui/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import { constructionSitesQueryKeys } from "../../../../api/query-keys/constructionSites.query-keys";
import { gfasQueryKeys } from "../../../../api/query-keys/gfas.query-keys";
import {
	downloadFileWithName,
	isConstructionSiteDocumentType,
} from "../../../../utils/document.utils";

interface DocumentsColumnsProps {
	constructionSiteId: number;
	loadingDocumentId: string | null;
	downloadingDocumentId: string | null;
	setDownloadingDocumentId: (id: string | null) => void;
	onDelete?: (documentId: string) => void;
	deletingDocumentId?: string | null;
	isDuneUser: boolean;
	isGFA: boolean;
}

export const documentsColumns = ({
	constructionSiteId,
	loadingDocumentId,
	downloadingDocumentId,
	setDownloadingDocumentId,
	onDelete,
	deletingDocumentId,
	isDuneUser,
	isGFA,
}: DocumentsColumnsProps): ColumnDef<
	ConstructionSiteDocument | GFADocument
>[] => {
	const statusConfig: Record<
		string,
		{ label: string; color: "orange" | "green" | "red" }
	> = {
		TO_BE_PROCESSED: { label: "À traiter", color: "orange" },
		COMPLIANT: { label: "Conforme", color: "green" },
		NON_COMPLIANT: { label: "Non conforme", color: "red" },
	};

	const { mutate: unlinkFile, isPending: isUnlinkingDoc } =
		useUnlinkFileFromConstructionSiteSubmission(constructionSiteId);

	return [
		{
			accessorKey: "name",
			header: "Nom",
			cell: ({ row }) => {
				const doc = row.original;
				const docId = doc.id.toString();
				const isLoadingDoc = loadingDocumentId === docId;

				return (
					<div className="flex items-center gap-3 min-w-0">
						{isLoadingDoc ? (
							<Loader2 size={16} className="animate-spin flex-shrink-0" />
						) : (
							<FileText size={16} className="flex-shrink-0" />
						)}
						<span className="truncate max-w-xs" title={doc.name}>
							{doc.name}
						</span>
					</div>
				);
			},
		},
		{
			accessorKey: "type",
			header: "Type",
			cell: ({ row }) => {
				const doc = row.original;
				const label = isConstructionSiteDocumentType(doc.type)
					? constructionSiteDocumentTypeLabels[doc.type]
					: gfaDocumentTypeLabels[doc.type];
				return (
					<div className="inline-flex items-center px-2 py-1 rounded-full text-xs text-gray-badge-text bg-gray-badge-background border border-gray-badge-border whitespace-nowrap">
						{label}
					</div>
				);
			},
		},
		{
			accessorKey: "author",
			header: "Créé par",
			cell: ({ row }) => {
				const doc = row.original;
				const author = doc.author;
				return author ? (
					<div
						className="text-sm text-gray-600 truncate max-w-24"
						title={`${author.firstname} ${author.lastname}`}
					>
						{author.firstname} {author.lastname}
					</div>
				) : (
					<div>—</div>
				);
			},
		},
		{
			accessorKey: "updatedAt",
			header: "Modifié le",
			cell: ({ row }) => {
				const doc = row.original;
				return (
					<div className="text-sm whitespace-nowrap">
						{format(new Date(doc.updatedAt), "dd/MM/yy")}
					</div>
				);
			},
		},
		{
			accessorKey: "status",
			header: "Statut",
			cell: ({ row }) => {
				const doc = row.original;
				const status = statusConfig[doc.status] || {
					label: doc.status,
					color: "orange" as const,
				};

				const getStatusClasses = (color: "orange" | "green" | "red") => {
					switch (color) {
						case "orange":
							return "text-orange-badge-text bg-orange-badge-background border-orange-badge-border";
						case "green":
							return "text-green-badge-text bg-green-badge-background border-green-badge-border";
						case "red":
							return "text-red-badge-text bg-red-badge-background border-red-badge-border";
						default:
							return "text-orange-badge-text bg-orange-badge-background border-orange-badge-border";
					}
				};

				return (
					<div
						className={`inline-flex items-center px-2 py-1 rounded-full text-xs border whitespace-nowrap ${getStatusClasses(doc.isDeleted ? "red" : status.color)}`}
					>
						{doc.isDeleted ? "Supprimé" : status.label}
					</div>
				);
			},
		},
		{
			accessorKey: "isReferenced",
			header: "Référencé ?",
			cell: ({ row }) => {
				const doc = row.original;
				return doc.isReferenced ? (
					<Chip label="Référencé" color="green" />
				) : (
					<Chip label="Non" color="red" />
				);
			},
		},
		{
			id: "actions",
			header: "Actions",
			cell: ({ row }) => {
				const doc = row.original;
				const docId = doc.id.toString();
				const isDownloadingDoc = downloadingDocumentId === docId;
				const isDeletingDoc = deletingDocumentId === docId;

				const { data: documentUrl, refetch: fetchDocumentUrl } = useDocumentUrl(
					{
						bucket: doc.bucket,
						key: doc.key,
						documentId: docId,
					},
					false,
				);

				const handleDownload = useCallback(
					async (e: React.MouseEvent) => {
						e.stopPropagation();

						if (isDownloadingDoc) return;

						setDownloadingDocumentId(docId);

						try {
							let url = documentUrl;
							if (!url) {
								const result = await fetchDocumentUrl();
								url = result.data;
							}

							if (url) {
								await downloadFileWithName(url, doc.name);
							}
						} catch (error) {
							console.error(
								"Erreur lors du téléchargement du document:",
								error,
							);
						} finally {
							setDownloadingDocumentId(null);
						}
					},
					[
						docId,
						documentUrl,
						fetchDocumentUrl,
						isDownloadingDoc,
						setDownloadingDocumentId,
						doc.name,
					],
				);
				const queryClient = useQueryClient();
				const handleUnlink = useCallback(
					(e: React.MouseEvent) => {
						e.stopPropagation();
						if (isUnlinkingDoc) return;

						unlinkFile(docId, {
							onSuccess: () => {
								queryClient.invalidateQueries({
									queryKey: isGFA
										? gfasQueryKeys.documents(constructionSiteId)
										: constructionSitesQueryKeys.documents(constructionSiteId),
								});
							},
						});
					},
					[docId, constructionSiteId, queryClient, isGFA],
				);

				const handleDelete = useCallback(
					(e: React.MouseEvent) => {
						e.stopPropagation();
						if (isDeletingDoc || !onDelete) return;

						onDelete(docId);
					},
					[docId, isDeletingDoc, onDelete],
				);

				return (
					<div className="flex justify-start gap-1">
						<Button
							variant="ghost"
							size="sm"
							className="h-8 w-8 p-0"
							aria-label="download"
							disabled={isDownloadingDoc}
							onClick={handleDownload}
						>
							{isDownloadingDoc ? (
								<Loader2 size={14} className="animate-spin" />
							) : (
								<Download size={14} />
							)}
						</Button>
						{doc.isReferenced && (
							<Button
								variant="ghost"
								size="sm"
								className="h-8 w-8 p-0"
								aria-label="unlink"
								disabled={isUnlinkingDoc}
								onClick={handleUnlink}
							>
								<LinkBroken02 />
							</Button>
						)}
						{onDelete && isDuneUser && (
							<Button
								variant="ghost"
								size="sm"
								className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
								aria-label="delete"
								disabled={isDeletingDoc}
								onClick={handleDelete}
							>
								{isDeletingDoc ? (
									<Loader2 size={14} className="animate-spin" />
								) : (
									<Trash2 size={14} />
								)}
							</Button>
						)}
					</div>
				);
			},
		},
	];
};
